import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import sizeService from "./sizeService";

const buildErrorMessage = (error) => {
    if (!error) return "Unknown error";
    if (error.response && error.response.data) {
        return error.response.data.message || error.response.data || error.message;
    }
    return error.message || String(error);
};

export const getSizes = createAsyncThunk(
    "productSize/get-sizes",
    async (_, thunkAPI) => {
        try {
            return await sizeService.getSizes();
        } catch (error) {
            return thunkAPI.rejectWithValue(buildErrorMessage(error));
        }
    }
);

export const createSize = createAsyncThunk(
    "productSize/create-size",
    async (sizeData, thunkAPI) => {
        try {
            return await sizeService.createSize(sizeData);
        } catch (error) {
            return thunkAPI.rejectWithValue(buildErrorMessage(error));
        }
    }
);

export const updateSize = createAsyncThunk(
    "productSize/update-size",
    async (size, thunkAPI) => {
        try {
            return await sizeService.updateSize(size);
        } catch (error) {
            return thunkAPI.rejectWithValue(buildErrorMessage(error));
        }
    }
);

export const deleteSize = createAsyncThunk(
    "productSize/delete-size",
    async (id, thunkAPI) => {
        try {
            return await sizeService.deleteSize(id);
        } catch (error) {
            return thunkAPI.rejectWithValue(buildErrorMessage(error));
        }
    }
);

export const getSize = createAsyncThunk(
    "productSize/get-product-size",
    async (id, thunkAPI) => {
        try {
            return await sizeService.getSize(id);
        } catch (error) {
            return thunkAPI.rejectWithValue(buildErrorMessage(error));
        }
    }
);

export const resetState = createAction("RevertAll");

const initialState = {
    sizes: [],
    isError: false,
    isLoading: false,
    isSuccess: false,
    message: "",
};

export const sizeSlice = createSlice({
    name: "sizes",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSizes.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getSizes.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isSuccess = true;
                state.sizes = action.payload;
            })
            .addCase(getSizes.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.isSuccess = false;
                state.message = action.payload || action.error?.message;
            })
            .addCase(createSize.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createSize.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isSuccess = true;
                state.createdTag = action.payload;
            })
            .addCase(createSize.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.isSuccess = false;
                state.message = action.payload || action.error?.message;
            })
            .addCase(updateSize.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateSize.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isSuccess = true;
                state.updatedSize = action.payload;
            })
            .addCase(updateSize.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.isSuccess = false;
                state.message = action.payload || action.error?.message;
            })
            .addCase(deleteSize.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteSize.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isSuccess = true;
                state.deletedSize = action.payload;
            })
            .addCase(deleteSize.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.isSuccess = false;
                state.message = action.payload || action.error?.message;
            })
            .addCase(getSize.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getSize.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isError = false;
                state.isSuccess = true;
                state.sizeName = action.payload.title;
            })
            .addCase(getSize.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.isSuccess = false;
                state.message = action.payload || action.error?.message;
            })
            .addCase(resetState, () => initialState);
    },
});

export default sizeSlice.reducer;
